import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  STAFF,
  STAFF_ALL,
  STAFF_ALL_FILTERED,
  STAFF_DELETE,
  STAFF_DEPARTMENT_POSITION,
  STAFF_FULL_INFO,
  STAFF_FULL_INFO_UPDATE,
  STAFF_HR_INFO,
  STAFF_HR_INFO_UPDATE,
  STAFF_LIST,
  STAFF_PERSONAL_INFO,
  STAFF_PROOF_OF_WORK_PDF,
  STAFF_USERNAME,
} from '../constants/actionTypes'
import * as API from '../constants/api'
import { PUBLIC_STAFF_REF } from '../constants/route'

export const staff = createAsyncThunk(
  STAFF,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_INFO}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.staff(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffDelete = createAsyncThunk(
  STAFF_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.STAFF}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.staffDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffList = createAsyncThunk(
  STAFF_LIST,
  async (params: PayloadCreatorParams<`${typeof API.STAFF}`, 'get'> | undefined, { rejectWithValue }) => {
    try {
      const response = await api.staffList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffAll = createAsyncThunk(
  STAFF_ALL,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_ALL}`, 'get'> | undefined, { rejectWithValue }) => {
    try {
      const response = await api.staffAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffAllFiltered = createAsyncThunk(
  STAFF_ALL_FILTERED,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_ALL_FILTERED}`, 'get'> | undefined, { rejectWithValue }) => {
    try {
      const response = await api.staffAllFiltered(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffPersonalInfoUpdate = createAsyncThunk(
  STAFF_PERSONAL_INFO,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_PERSONAL_INFO}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.staffPersonalInfoUpdate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffFullInfoUpdate = createAsyncThunk(
  STAFF_FULL_INFO_UPDATE,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_FULL_INFO}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.staffFullInfoUpdate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffFullInfo = createAsyncThunk(
  STAFF_FULL_INFO,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_FULL_INFO}/{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.staffFullInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffHrInfoUpdate = createAsyncThunk(
  STAFF_HR_INFO_UPDATE,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_HR_INFO}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.staffHrInfoUpdate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffHrInfo = createAsyncThunk(
  STAFF_HR_INFO,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_HR_INFO}/{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.staffHrInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffUsername = createAsyncThunk(
  STAFF_USERNAME,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_USERNAME}{username}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.staffUsername(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffDepartmentPosition = createAsyncThunk(
  STAFF_DEPARTMENT_POSITION,
  async (
    params: PayloadCreatorParams<`${typeof API.STAFF_DEPARTMENT_POSITION}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.staffDepartmentPosition(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffProofOfWorkGenerate = createAsyncThunk(
  STAFF_PROOF_OF_WORK_PDF,
  async (params: PayloadCreatorParams<`${typeof API.STAFF_PROOF_OF_WORK_PDF}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.staffReference(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const publicStaffRef = createAsyncThunk(
  PUBLIC_STAFF_REF,
  async (params: PayloadCreatorParams<`${typeof API.PUBLIC_STAFF_REF}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.publicStaffRef(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const staffClear = createAction(`${STAFF}/clear`)

export const staffListClear = createAction(`${STAFF_LIST}/clear`)

export const staffAllClear = createAction(`${STAFF_ALL}/clear`)

export const staffAllFilteredClear = createAction(`${STAFF_ALL_FILTERED}/clear`)

export const staffUsernameClear = createAction(`${STAFF_USERNAME}/clear`)

export const staffDepartmentPositionClear = createAction(`${STAFF_DEPARTMENT_POSITION}/clear`)
